// https://github.com/radix-ui/primitives/issues/1836#issuecomment-2050627695

import { useState } from "react";

import { type DispatchAgoraEventProps, dispatchAgoraEvent } from "@/lib/utils/dispatch-agora-event";

export interface UseDialogReturn<T> {
  data: null | T;
  dismiss: () => void;
  props: {
    onOpenChange: (open: boolean) => void;
    open: boolean;
  };
  trigger: (payload?: T) => void;
}

export interface UseDialogOptions<T = void> {
  eventProps?: Partial<DispatchAgoraEventProps>;
  /**
   * Derives extra event props from the current payload. Merged over
   * `eventProps`, so payload-derived fields win. Runs on both open and
   * close — `data` is still the last triggered payload on close.
   */
  getEventProps?: (data: null | T) => Partial<DispatchAgoraEventProps>;
}

export function useDialog<T = void>(options?: UseDialogOptions<T>): UseDialogReturn<T> {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<null | T>(null);
  const { eventProps, getEventProps } = options ?? {};

  function handleOpenChange(open: boolean, payload?: null | T) {
    setIsOpen(open);

    const resolved = getEventProps?.(payload === undefined ? data : payload) ?? {};

    if (open) {
      dispatchAgoraEvent({
        eventType: "product.clicked",
        eventLocation: "body",
        eventAction: "modal.opened",
        ...eventProps,
        ...resolved,
      });
    } else {
      dispatchAgoraEvent({
        eventType: "product.clicked",
        eventLocation: "modal",
        eventAction: "modal.closed",
        ...eventProps,
        ...resolved,
      });
    }
  }

  function trigger(payload?: T) {
    const next = payload ?? null;
    setData(next);
    handleOpenChange(true, next);
  }

  return {
    data,
    dismiss: () => handleOpenChange(false),
    props: { onOpenChange: handleOpenChange, open: isOpen },
    trigger,
  };
}
